import { Button, Stack, Typography } from "@mui/material";
import type {
  BaseCategory,
  DefaultLabels,
} from "@usercentrics/cmp-browser-sdk";
import { useMemo, useState } from "react";

import {
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogTitle,
} from "@dfp/react-components";

import { CategoryAccordions } from "./CategoryAccordions.tsx";
import { htmlToParagraphs } from "./htmlToParagraphs.ts";

type ConsentSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  labels: DefaultLabels | null;
  categories: BaseCategory[];
  isCategoryEnabled: (category: BaseCategory) => boolean;
  onToggleCategory: (category: BaseCategory, enabled: boolean) => void;
  submitting: boolean;
  onDenyNonEssential: () => void;
  onSave: () => void;
  onAcceptAll: () => void;
};

/** Second consent layer: detailed description and per-category toggles in a dialog. */
export function ConsentSettingsDialog({
  open,
  onClose,
  labels,
  categories,
  isCategoryEnabled: categoryEnabled,
  onToggleCategory,
  submitting,
  onDenyNonEssential,
  onSave,
  onAcceptAll,
}: ConsentSettingsDialogProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const descriptionParagraphs = useMemo(
    () => htmlToParagraphs(labels?.secondLayer.description ?? ""),
    [labels],
  );

  const cookiePolicyLink = labels?.links.cookiePolicy;
  const imprintLink = labels?.links.imprint;
  // Empty second-layer label overrides fall back to the shared button labels.
  const denyNonEssentialLabel =
    labels?.secondLayer.denyButtonLabel || labels?.buttons.denyAll;
  const acceptAllLabel =
    labels?.secondLayer.acceptButtonLabel || labels?.buttons.acceptAll;

  return (
    <AppDialog open={open} onClose={() => onClose()}>
      <AppDialogTitle sx={{ pr: 7 }}>
        {labels?.secondLayer.title}
      </AppDialogTitle>
      <AppDialogContent>
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            {descriptionParagraphs.map((paragraph, index) => (
              <Typography key={index} variant="body2">
                {paragraph}
              </Typography>
            ))}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {cookiePolicyLink?.url && (
                <Button
                  variant="text"
                  size="small"
                  href={cookiePolicyLink.url}
                  target="_blank"
                  rel="noopener"
                >
                  {cookiePolicyLink.label}
                </Button>
              )}
              {imprintLink?.url && (
                <Button
                  variant="text"
                  size="small"
                  href={imprintLink.url}
                  target="_blank"
                  rel="noopener"
                >
                  {imprintLink.label}
                </Button>
              )}
            </Stack>
          </Stack>
          <CategoryAccordions
            categories={categories}
            expanded={expanded}
            onToggleExpanded={(slug) =>
              setExpanded((prev) => (prev === slug ? null : slug))
            }
            isCategoryEnabled={categoryEnabled}
            onToggleCategory={onToggleCategory}
          />
        </Stack>
      </AppDialogContent>
      <AppDialogActions
        sx={{ gap: 2, flexDirection: { xs: "column", sm: "row" } }}
      >
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          disabled={submitting}
          onClick={onDenyNonEssential}
          sx={{ whiteSpace: "normal" }}
        >
          {denyNonEssentialLabel}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          disabled={submitting}
          onClick={onSave}
          sx={{ whiteSpace: "normal" }}
        >
          {labels?.buttons.save}
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={submitting}
          onClick={onAcceptAll}
          sx={{ whiteSpace: "normal" }}
        >
          {acceptAllLabel}
        </Button>
      </AppDialogActions>
    </AppDialog>
  );
}
